import React, { useState, useEffect } from "react";
import cx from "classnames";
import { useAnimation, m, AnimatePresence } from "framer-motion";
import useInView from "react-cool-inview";

import Icon from "@components/icon";
import Photo from "@components/photo";
import Phrase from "@components/phrase";
import Accordion from "@components/accordion";
import Media from "@components/media";
const FeatureCarousel = ({ data = {} }) => {
  const { phrase, slides } = data;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [numSlides, setNumSlides] = useState(slides.length - 1);
  const [activeAccordions, setActiveAccordions] = useState([
    `carousel-dropdown-0`,
  ]);
  const buttonVariants = {
    hidden: {
      opacity: 0,
      y: "25px",
      transition: {
        duration: 0.25,
        ease: "easeOut",
      },
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.25,
        ease: "easeOut",
      },
    },
  };
  const buttonContainerVariants = {
    hidden: {
      opacity: 0,
      transition: {
        staggerChildren: 0.25,
        ease: "easeOut",
      },
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.25,
        ease: "easeOut",
      },
    },
  };
  const titleVariants = {
    hidden: {
      opacity: 0,
      y: "100px",
      transition: {
        duration: 1,
        ease: "easeOut",
      },
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1,
        ease: "easeOut",
      },
    },
  };
  const controls = useAnimation();
  const { observe, unobserve, inView, scrollDirection, entry } = useInView(
    {
      rootMargin: "-100px 0px",
      threshold: [0.2, 0.4, 0.6],
      onEnter: ({ scrollDirection }) => {
        controls.start("visible");
      },
      // onLeave: ({ scrollDirection }) => {
      //   if (scrollDirection.vertical == "down") {
      //     controls.start("hidden");
      //   }
      // },
    },
    [controls]
  );

  const onToggle = (id, status) => {
    if (activeAccordions.includes(id)) {
      setActiveAccordions(activeAccordions.filter((item) => item !== id));
    } else {
      setActiveAccordions([...activeAccordions, id]);
    }
  };
  const moveCarousel = (direction) => {
    if (direction == "next") {
      currentSlide = currentSlide + 1;
      if (currentSlide > numSlides) {
        currentSlide = 0;
      }
    } else {
      currentSlide = currentSlide - 1;
      if (currentSlide < 0) {
        currentSlide = numSlides;
      }
    }
    setCurrentSlide(currentSlide);
  };

  return (
    <section className={cx("feature-carousel")} ref={observe}>
      <div className={cx("grid", "px-gutter", "md:hidden", "site-container")}>
        <div className="text-lg-1-mobile md:text-lg-1 md:pl-1">{phrase}</div>
      </div>
      <div
        className={cx(
          "px-gutter",
          "hidden",
          "md:grid",
          "md:mb-[100px]",
          "site-container"
        )}
      >
        <m.div variants={titleVariants} initial="hidden" animate={controls}>
          <div className="text-lg-1-mobile md:text-lg-1 md:pl-1">{phrase}</div>
        </m.div>
      </div>
      <div
        className={cx(
          "grid",
          "px-gutter",
          "md:px-5",
          "pt-10",
          "pb-3",
          "site-container"
        )}
      >
        <div className={cx("expanders", "block", "md:hidden")}>
          {slides.map((slide, idx) => (
            <Accordion
              key={idx}
              style="mobile-alt"
              title={slide.slideLabel}
              id={`carousel-dropdown-${idx}`}
              isOpen={activeAccordions.includes(`carousel-dropdown-${idx}`)}
              onToggle={onToggle}
            >
              <div className={cx("text-large")}>
                <div
                  className={cx(
                    "aspect-square",
                    "md:aspect-video",
                    "relative",
                    "overflow-hidden",
                    "mb-4"
                  )}
                >
                  <Media media={slide.media} aspect="square" layout="fill" />
                </div>
                {slide.slideBody}
              </div>
            </Accordion>
          ))}
        </div>
        <m.div
          className={cx("slide-nav", "hidden", "md:block")}
          variants={buttonContainerVariants}
          initial="hidden"
          animate={controls}
        >
          <div>
            {slides.map(({ slideLabel }, idx) => (
              <m.div
                className={cx("inline-flex", "mr-6")}
                variants={buttonVariants}
                key={idx}
              >
                <button
                  className={cx(
                    "text-caption",
                    "md:text-subheader-2",
                    "uppercase",
                    "relative",
                    "font-sansRegular",
                    {
                      "opacity-50": currentSlide !== idx,
                    }
                  )}
                  onClick={() => setCurrentSlide(idx)}
                >
                  {slideLabel}
                  <div
                    className={cx(
                      "border-t",
                      "absolute",
                      "bottom-[-2px]",
                      "left-0",
                      "transition-all",
                      {
                        "w-full": currentSlide == idx,
                        "w-0": currentSlide !== idx,
                      }
                    )}
                  ></div>
                </button>
              </m.div>
            ))}
          </div>
        </m.div>
      </div>
      <div className={cx("slides", "hidden", "md:block", "site-container")}>
        <div className={cx("card", "m-2")}>
          <div
            className={cx(
              "grid",
              "p-1",
              "grid-cols-1",
              "md:grid-cols-20",
              "bg-white",
              "w-full",
              "rounded-[3px]"
            )}
          >
            <div className={cx("col-span-9", "flex", "flex-col", "p-2")}>
              <div
                className={cx(
                  "text-large",
                  "font-sansRegular",
                  "tracking-[0.01em]",
                  "leading-[1.3]",
                  "mb-10",
                  "md:mb-0",
                  "max-w-[67%]"
                )}
              >
                {slides[currentSlide].slideBody}
              </div>
              <div className={cx("carousel-controls", "mt-auto")}>
                <button
                  className={cx("w-[64px]", "mr-3")}
                  onClick={() => moveCarousel("prev")}
                >
                  <Icon
                    name="CarouselLeft"
                    viewBox="0 0 64 64"
                    color="#292929"
                  />
                </button>
                <button
                  className={cx("w-[64px]")}
                  onClick={() => moveCarousel("next")}
                >
                  <Icon
                    name="CarouselRight"
                    viewBox="0 0 64 64"
                    color="#292929"
                  />
                </button>
              </div>
            </div>
            <div className={cx("col-span-11")}>
              <div className={cx("aspect-video", "relative")}>
                {slides.map((slide, idx) => (
                  <div
                    key={idx}
                    className={cx(
                      "absolute",
                      "top-0",
                      "left-0",
                      "w-full",
                      "h-full",
                      "transition-opacity",
                      {
                        "opacity-0": currentSlide !== idx,
                        "opacity-100": currentSlide == idx,
                      }
                    )}
                  >
                    <Media media={slide.media} aspect="video" layout="fill" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureCarousel;
